import { BasinModel } from './climate-geometry';

/** Same noise family and elevation formula as the surface shader. Sampling is
 * statistical: CPU/GPU rounding and the finite sample set are not a fluid mesh. */
const cache = new Map<number, BasinModel>();
export function terrainBasins(seed: number): BasinModel {
  let basin = cache.get(seed);
  if (basin !== undefined) return basin;
  const heights: number[] = [];
  for (let i = 0; i < 1024; i += 1) {
    const y = 1 - 2 * (i + 0.5) / 1024;
    const radius = Math.sqrt(1 - y * y), angle = i * Math.PI * (3 - Math.sqrt(5));
    heights.push(terrainHeight(radius * Math.cos(angle), y, radius * Math.sin(angle), seed));
  }
  basin = new BasinModel(heights);
  cache.set(seed, basin);
  return basin;
}

function fract(x: number): number { return x - Math.floor(x); }
function mix(a: number, b: number, t: number): number { return a + (b - a) * t; }
function hash(x: number, y: number, z: number): number {
  const f = Math.fround;
  x = fract(f(x * f(0.1031))); y = fract(f(y * f(0.1031))); z = fract(f(z * f(0.1031)));
  const dot = f(f(f(x * f(y + f(33.33))) + f(y * f(z + f(33.33)))) + f(z * f(x + f(33.33))));
  x = f(x + dot); y = f(y + dot); z = f(z + dot);
  return fract(f(f(x + y) * z));
}
function noise(x: number, y: number, z: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  x -= ix; y -= iy; z -= iz;
  x = x * x * (3 - 2 * x); y = y * y * (3 - 2 * y); z = z * z * (3 - 2 * z);
  return mix(mix(mix(hash(ix, iy, iz), hash(ix + 1, iy, iz), x),
    mix(hash(ix, iy + 1, iz), hash(ix + 1, iy + 1, iz), x), y),
  mix(mix(hash(ix, iy, iz + 1), hash(ix + 1, iy, iz + 1), x),
    mix(hash(ix, iy + 1, iz + 1), hash(ix + 1, iy + 1, iz + 1), x), y), z);
}
function fbm(x: number, y: number, z: number): number {
  let signal = 0, amplitude = 0.5;
  for (let octave = 0; octave < 3; octave += 1) {
    signal += noise(x, y, z) * amplitude;
    const nx = (-0.8 * y - 0.6 * z) * 2.03 + 4.17;
    const ny = (0.8 * x + 0.36 * y - 0.48 * z) * 2.03 - 2.31;
    z = (0.6 * x - 0.48 * y + 0.64 * z) * 2.03 + 6.43;
    x = nx; y = ny; amplitude *= 0.5;
  }
  return signal;
}
export function terrainHeight(x: number, y: number, z: number, seed: number): number {
  x = x * (4.2 + seed * 2.1) + seed * 17;
  y = y * (3.7 + seed) + seed * 17;
  z = z * (4 + seed * 1.4) + seed * 17;
  return Math.max(0, Math.min(1, 0.5 + (fbm(x, y, z) * 0.72 +
    fbm(x * 2.8 + seed * 31, y * 2.8 + seed * 31, z * 2.8 + seed * 31) * 0.28 - 0.4375) * 2.4));
}

export const TERRAIN_SIGNALS_GLSL = `vec3 terrainSignals(vec3 normal, float seed) {
  vec3 mapPoint = normal * vec3(4.2 + seed * 2.1, 3.7 + seed, 4.0 + seed * 1.4) + seed * 17.0;
  float broadTerrain = fbm3D(mapPoint);
  float fineTerrain = fbm3D(mapPoint * 2.8 + seed * 31.0);
  float elevation = clamp(0.5 + (broadTerrain * 0.72 + fineTerrain * 0.28 - 0.4375) * 2.4, 0.0, 1.0);
  return vec3(elevation, broadTerrain, fineTerrain);
}`;
